"use client";

import { HealthCompanionClient, type HealthCompanionClientOptions } from "@health/sdk-client";
import { createContext, useContext, useRef } from "react";
import { type CompanionHostContextValue, CompanionHostProvider } from "./host";

type ChatProviderValue = {
  client: HealthCompanionClient;
};

const ChatContext = createContext<ChatProviderValue | null>(null);

export type ChatProviderProps = {
  children: React.ReactNode;
  clientOptions?: HealthCompanionClientOptions;
  host?: CompanionHostContextValue;
};

export function ChatProvider({ children, clientOptions, host = {} }: ChatProviderProps) {
  const clientRef = useRef<HealthCompanionClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new HealthCompanionClient(clientOptions);
  }

  return (
    <CompanionHostProvider value={host}>
      <ChatContext.Provider value={{ client: clientRef.current }}>{children}</ChatContext.Provider>
    </CompanionHostProvider>
  );
}

export function useChatClient() {
  const value = useContext(ChatContext);
  if (!value) {
    throw new Error("useChatClient must be used within ChatProvider");
  }

  return value.client;
}

/**
 * Like `useChatClient` but returns `null` instead of throwing when no
 * `ChatProvider` is mounted. Use this in code paths that may render in
 * offline/mock surfaces (e.g. mock-chat, Storybook) where a real client
 * isn't available.
 */
export function useOptionalChatClient(): HealthCompanionClient | null {
  return useContext(ChatContext)?.client ?? null;
}
